import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ErrorCode } from '@safetyhub/shared';
import { useGroupStore } from '../stores/group';

// WebSocket socket ID 제공자 (순환 참조 방지: websocket.ts가 이 함수를 등록)
let socketIdProvider: (() => string | null) | null = null;

/**
 * socket ID 제공자 등록.
 * websocket.ts에서 호출하여 api.ts가 socket ID를 알 수 있게 함.
 * (api.ts → websocket.ts 직접 import 시 순환 참조 발생하므로 콜백 방식 사용)
 */
export function registerSocketIdProvider(provider: () => string | null): void {
  socketIdProvider = provider;
}

// API 버전 (버전 변경 시 이 값만 수정)
const API_VERSION = 'v1';

// API 기본 URL
export const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const API_BASE_URL = `${API_HOST}/api/${API_VERSION}`;

// Axios 인스턴스 생성
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// 토큰 관리
const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const IMPERSONATION_TOKEN_KEY = 'impersonationToken';

export const tokenManager = {
  // 일반 로그인 토큰 (localStorage)
  getAccessToken: () => localStorage.getItem(TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  // 빙의 토큰 (sessionStorage - 탭별 격리)
  getImpersonationToken: () => sessionStorage.getItem(IMPERSONATION_TOKEN_KEY),
  setImpersonationToken: (token: string) => {
    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
  },
  clearImpersonationToken: () => {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  },
  isImpersonating: () => !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY),

  // 현재 유효한 토큰 (빙의 우선)
  getCurrentToken: () => {
    return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  },
};

// 요청 인터셉터: Authorization 헤더 추가 + X-Socket-Id 헤더 추가
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // skipAuth 플래그가 있으면 인증 헤더 추가하지 않음 (공개 API용)
    const skipAuth = (config as InternalAxiosRequestConfig & { skipAuth?: boolean }).skipAuth;
    if (skipAuth) {
      return config;
    }

    // 빙의 토큰 우선 사용
    const token = tokenManager.getCurrentToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // WebSocket 연결 ID 헤더 추가 (서버 push 토스트 중복 방지용)
    const socketId = socketIdProvider?.();
    if (socketId && config.headers) {
      config.headers['X-Socket-Id'] = socketId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터: 토큰 만료 시 갱신
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

// 네트워크/서버 에러 재시도 설정
const NETWORK_RETRY_BASE_DELAY = 1000; // 기본 1초
const NETWORK_MAX_RETRIES = 10; // 최대 10회 재시도
const NETWORK_RETRY_MAX_DELAY = 8000; // 최대 8초 간격

// Idempotent HTTP 메서드 (재시도 안전)
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

/** 지수 backoff 대기: min(base * 2^retryCount, maxDelay) */
const getRetryDelay = (retryCount: number): number =>
  Math.min(NETWORK_RETRY_BASE_DELAY * Math.pow(2, retryCount), NETWORK_RETRY_MAX_DELAY);

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// 토큰 갱신 완료 리스너 (WebSocket 재연결 등 외부 모듈에서 구독)
type TokenRefreshedListener = (newToken: string) => void;
const tokenRefreshedListeners: TokenRefreshedListener[] = [];

export const onTokenRefreshed = (listener: TokenRefreshedListener): (() => void) => {
  tokenRefreshedListeners.push(listener);
  return () => {
    const idx = tokenRefreshedListeners.indexOf(listener);
    if (idx !== -1) tokenRefreshedListeners.splice(idx, 1);
  };
};

const notifyTokenRefreshed = (newToken: string) => {
  tokenRefreshedListeners.forEach((listener) => listener(newToken));
};

/**
 * Refresh token으로 access token을 갱신합니다.
 * WebSocket 등 axios 인터셉터 밖에서 토큰 갱신이 필요한 경우 사용합니다.
 * 갱신 성공 시 새 access token을 반환하고 tokenRefreshed 리스너에 알립니다.
 * 갱신 실패 시 null을 반환합니다 (토큰 삭제/리다이렉트는 호출부에서 처리).
 * 동시 호출 시 하나의 갱신 Promise를 공유하여 이중 갱신을 방지합니다.
 */
let refreshPromise: Promise<string | null> | null = null;

export const refreshAccessToken = async (): Promise<string | null> => {
  // 이미 갱신 중이면 기존 Promise 대기 (이중 갱신 방지)
  if (refreshPromise) return refreshPromise;

  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) return null;

  refreshPromise = (async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      tokenManager.setTokens(accessToken, newRefreshToken);
      notifyTokenRefreshed(accessToken);
      return accessToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// 다른 탭의 토큰 변경 감지 (storage 이벤트는 변경한 탭이 아닌 다른 탭에서만 발생)
window.addEventListener('storage', (e) => {
  if (e.key === TOKEN_KEY && e.newValue && isRefreshing) {
    // 다른 탭이 새 access token을 저장함 → 대기 큐 해제
    processQueue(null, e.newValue);
    isRefreshing = false;
  }
  if (e.key === TOKEN_KEY && !e.newValue) {
    // 다른 탭이 토큰을 삭제함 (로그아웃) → 이 탭도 로그아웃
    const publicPrefixes = ['/approval/', '/share/', '/tbm/sign/', '/educations/tbm/sign/', '/invite/', '/release-notes'];
    const isPublicPage = publicPrefixes.some((p) => window.location.pathname.startsWith(p));
    if (!isPublicPage) {
      window.location.href = '/login';
    }
  }
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _networkRetryCount?: number;
    };

    // 네트워크 에러(서버 다운) 또는 5xx 에러인 경우 자동 재시도
    // POST/PATCH는 중복 생성 위험으로 기본 제외. Idempotent 메서드(GET/HEAD/OPTIONS/PUT/DELETE)만 재시도
    const isNetworkError = !error.response || error.code === 'ERR_NETWORK';
    const isServerError =
      error.response?.status !== undefined &&
      error.response.status >= 500 &&
      error.response.status < 600;
    const isRetryable = isNetworkError || isServerError;
    const method = (originalRequest?.method || '').toUpperCase();
    const isIdempotent = IDEMPOTENT_METHODS.has(method);

    if (isRetryable && isIdempotent && originalRequest) {
      const retryCount = originalRequest._networkRetryCount || 0;
      if (retryCount < NETWORK_MAX_RETRIES) {
        originalRequest._networkRetryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount);
        if (retryCount === 0) {
          // 첫 번째 재시도 시에만 로그 출력 (이후 과도한 로그 억제)
          console.log(
            `[api.ts] ${isServerError ? `HTTP ${error.response?.status}` : 'Network error'}, retrying (${retryCount + 1}/${NETWORK_MAX_RETRIES}) in ${delay}ms...`
          );
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(originalRequest);
      }
      console.log('[api.ts] Retryable error, max retries exceeded');
    }

    // 401 에러이고, 재시도하지 않은 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('[api.ts] Received 401 for:', originalRequest.url);
      // 로그인/회원가입 요청은 토큰 갱신 시도하지 않음
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/signup')
      ) {
        return Promise.reject(error);
      }

      // 빙의 중인 경우: 토큰 갱신 없이 빙의 종료
      if (tokenManager.isImpersonating()) {
        tokenManager.clearImpersonationToken();
        window.close();
        // window.close()가 동작하지 않는 경우
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // 이미 갱신 중이면 큐에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // refresh 시도 전 현재 RT를 기억 (다른 탭 갱신 감지용)
      const usedRefreshToken = tokenManager.getRefreshToken();

      try {
        if (!usedRefreshToken) {
          console.log('[api.ts] No refresh token available, redirecting to login');
          throw new Error('No refresh token');
        }

        console.log('[api.ts] Access token expired, attempting refresh...');
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: usedRefreshToken,
        });

        console.log('[api.ts] Refresh successful, new tokens received');
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        tokenManager.setTokens(accessToken, newRefreshToken);

        processQueue(null, accessToken);
        notifyTokenRefreshed(accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        console.log('[api.ts] Refresh failed:', refreshError);

        // 다른 탭이 이미 토큰을 갱신했는지 확인
        const currentRefreshToken = tokenManager.getRefreshToken();
        if (currentRefreshToken && currentRefreshToken !== usedRefreshToken) {
          // 다른 탭이 성공적으로 refresh → 새 토큰으로 재시도
          console.log('[api.ts] Another tab already refreshed, retrying with new token');
          const newAccessToken = tokenManager.getAccessToken();
          processQueue(null, newAccessToken);
          if (originalRequest.headers && newAccessToken) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          return api(originalRequest);
        }

        processQueue(refreshError as Error, null);

        // 네트워크 에러(서버 다운)는 토큰 유지, 인증 에러만 토큰 삭제
        const isRefreshNetworkError =
          axios.isAxiosError(refreshError) &&
          (!refreshError.response || refreshError.code === 'ERR_NETWORK');

        if (isRefreshNetworkError) {
          console.log('[api.ts] Network error during refresh, keeping tokens');
        } else {
          // 최종 확인: clearTokens 직전에 다시 한번 토큰 변경 여부 체크
          const latestRT = tokenManager.getRefreshToken();
          if (latestRT && latestRT !== usedRefreshToken) {
            console.log('[api.ts] Token changed just before clear, skipping logout');
            const latestAT = tokenManager.getAccessToken();
            if (originalRequest.headers && latestAT) {
              originalRequest.headers.Authorization = `Bearer ${latestAT}`;
            }
            return api(originalRequest);
          }
          console.log('[api.ts] Auth error during refresh, clearing tokens');
          tokenManager.clearTokens();
          const publicPrefixes = ['/approval/', '/share/', '/tbm/sign/', '/educations/tbm/sign/', '/invite/', '/release-notes'];
          const isPublicPage = publicPrefixes.some((p) => window.location.pathname.startsWith(p));
          if (!isPublicPage) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 그룹 접근 거부 자가 회복 — 남아 있던 타 회사/탈퇴 그룹 필터가 목록 API를 403으로 막을 때,
    // 필터를 비우면 currentGroupId를 구독하는 목록들이 dep 변화로 자동 재조회한다(원 요청은
    // 그대로 reject되지만 뒤이은 무필터 재조회가 대체한다). 필터가 없어졌으면 재조회는 200이라
    // 루프가 생기지 않는다.
    if (error.response?.status === 403) {
      const code = (error.response.data as ApiResponse<unknown> | undefined)?.error?.code;
      if (code === ErrorCode.GROUP_ACCESS_DENIED && useGroupStore.getState().currentGroupId) {
        useGroupStore.getState().setCurrentGroupId(null);
      }
    }

    return Promise.reject(error);
  }
);

// API 응답 타입
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  } | null;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
  };
}

// API 에러 타입
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// API 에러 추출 헬퍼
export const extractApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiResponse<unknown> | undefined;
    if (data?.error) {
      return data.error;
    }
    return {
      code: 'NETWORK_ERROR',
      message: error.message || '네트워크 오류가 발생했습니다.',
    };
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: '알 수 없는 오류가 발생했습니다.',
  };
};

/**
 * 서버가 실제로 내려준 안내 문구만 골라낸다(없으면 null).
 *
 * 결재라인 저장 거부처럼 **무엇이 잘못됐고 어느 칸인지**를 서버가 지목해 주는 응답이 있다
 * (예: `앞 결재란의 담당자가 비어 있으면 …`, `… 추가할 수 있는 결재란은 최대 1개입니다.`).
 * UI가 이 문구를 삼키고 "저장에 실패했습니다" 한 줄만 띄우면, 계약이 요구하는 안내가
 * 사용자에게 닿지 않는다 — 몇 개까지 되는지(S3), 어느 칸이 문제인지(S65)를 알 길이 없어진다.
 * (계약: `docs/decisions/2026-08-26-approval-line-user-stories.md`)
 *
 * 반대로 네트워크 단절·알 수 없는 예외의 문구는 "어느 칸이 문제다" 자리에 놓으면 오히려
 * 사용자를 헷갈리게 하므로 null을 반환해, 호출부가 자기 일반 문구로 폴백하게 한다.
 *
 * ## ⛔ 같은 이름이 세 자리다 — 사본이 아니라 «다른 계약»이고, 이 표가 그 분간의 정본이다
 *
 * 2026-10-27에 세 선언을 열어 재서 확정했다. 셋을 하나로 모으려 하지 말 것 — 읽는 봉투와
 * 폴백 소유가 다르다(「같은 이름이니 사본」이라는 읽기가 수렴 줄기를 한 번 잘못 출발시켰다).
 *
 * | 자리 | 읽는 봉투 | 폴백 소유 | 반환 |
 * |---|---|---|---|
 * | 이 함수(`lib/api.ts`) | `extractApiError` · NETWORK·UNKNOWN을 버린다 | **호출부**(`?? '…'`) | `string \| null` |
 * | `apps/admin/src/lib/apiErrorMessage.ts` | 같은 경유 · 그 둘을 `fallback`으로 | **헬퍼**(인자) | `string` |
 * | `lib/publicTokenApi.ts` | **공개 토큰 봉투 전용** — `error.details.message` 우선, `extractApiError`를 안 쓴다 | **헬퍼**(인자) | `string` |
 *
 * ⭐ **섞어 쓰는 것은 tsc가 막는다 — 그것이 이 축의 집행층이고 주석이 아니다.** 세 선언 모두
 * `fallback`이 **required**이거나 1-arg라, 다른 쪽 호출 형태를 쓰면 `TS2554`(인자 수 불일치)로
 * 그 자리에서 멈춘다. 그래서 문서가 어느 것인지 안 적어 틀려도 손해는 왕복 한 번이다.
 *
 * ⛔ **그 집행이 `fallback` required 하나에 매달려 있다** — 두 2-arg 선언 중 하나라도
 * `fallback?: string`으로 바꾸면 그날부터 `apiErrorMessage(err) ?? '…'`가 **컴파일되면서
 * `??`가 영영 안 타는** 조용한 결함이 된다(좌변이 이미 채워져 `null`이 오지 않는다). 폴백을
 * 선택적으로 만들고 싶으면 **이름을 가르는 쪽**으로 가라.
 *
 * @returns 서버 문구 / 서버가 준 문구가 아니거나 비었으면 null
 */
export const apiErrorMessage = (error: unknown): string | null => {
  const apiError = extractApiError(error);
  if (apiError.code === 'NETWORK_ERROR' || apiError.code === 'UNKNOWN_ERROR') return null;
  const message = apiError.message?.trim();
  return message ? message : null;
};

// HTTP 상태 코드 추출 헬퍼
export const getHttpStatus = (error: unknown): number | null => {
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? null;
  }
  return null;
};

/**
 * 파일 URL 정규화
 * DB에 저장된 localhost:3000 URL을 현재 API_HOST로 변환
 */
export const normalizeFileUrl = (url: string | null | undefined): string => {
  if (!url) return '';

  // localhost:3000을 현재 API_HOST로 교체
  if (url.includes('localhost:3000')) {
    return url.replace('http://localhost:3000', API_HOST);
  }

  return url;
};

// ============================================
// 일일 사용량 API
// ============================================

/** 일일 사용량 정보 */
export interface DailyUsageInfo {
  photoCount: number;
  photoLimit: number;
  inspectionCount: number;
  inspectionLimit: number;
  isEnabled: boolean;
  date: string; // YYYY-MM-DD (KST)
  nextResetAt: string; // ISO 8601 (다음 자정 KST)
}

/** 사용량 체크 결과 */
export interface UsageCheckResult {
  canProceed: boolean;
  currentCount: number;
  limit: number;
  remaining: number;
  isEnabled: boolean;
}

export const usageApi = {
  /**
   * 내 일일 사용량 조회
   * GET /api/v1/usage/daily
   */
  getDaily: async (): Promise<ApiResponse<DailyUsageInfo>> => {
    const response = await api.get<ApiResponse<DailyUsageInfo>>('/usage/daily');
    return response.data;
  },

  /**
   * 사용 가능 여부 체크
   * GET /api/v1/usage/check?type=photo|inspection
   */
  check: async (type: 'photo' | 'inspection'): Promise<ApiResponse<UsageCheckResult>> => {
    const response = await api.get<ApiResponse<UsageCheckResult>>(`/usage/check?type=${type}`);
    return response.data;
  },
};

// ============================================
// 릴리즈 노트 API (공개) — 「업데이트 내역」 화면이 쓴다
//
// ⚠️ **어휘가 둘이고 일부러 그렇다**: 사용자가 보는 문구는 **「업데이트 내역」**(2026-10-16 사용자
// 결정)이고, 경로(`/release-notes`)·타입·식별자는 `ReleaseNote*`로 **그대로 둔다**(서버 enum·DTO·
// 레포 파일 층 `release-notes/notes/*.json`이 그 이름이라 여기만 바꾸면 양쪽이 어긋난다).
// 즉 아래 주석의 「릴리즈 노트」는 **데이터·계약**을 가리킨다 — 화면 문구로 베껴 쓰지 말 것.
// ============================================

/**
 * 릴리즈 노트의 surface 값 — **서버 Prisma enum `ReleaseNotePlatform`의 손열거 사본**이다
 * (`apps/server/prisma/schema.prisma`가 정본이고 둘을 묶는 동기화 수단이 없다).
 *
 * 값이 일곱이고 성질이 셋으로 갈린다:
 * - **오늘의 surface**: `WEB` · `APP` · `ADMIN` (결정 `2026-10-15-release-note-surfaces-as-a-set-and-popup-decisions.md` §A ②)
 * - **유산**: `ALL`(「전부」 태그 — §A ④로 데이터에서 걷었다) · `MOBILE`(`APP`의 선행 값) · `SERVER`(§A ③으로 「영향받는 surface에 귀속」)
 * - **공집합**: `INTERNAL` — **아무의 공개 목록에도 뜨지 않는다**. 공개 피드의 집행은 서버
 *   `ReleaseNotesService.getPublicReleaseNotes`의 `scope` 파생점 한 자리가 소유한다
 *   (결정 `2026-10-15-internal-is-an-explicit-empty-surface-and-the-server-owns-its-enforcement.md`)
 *
 * ⚠️ **유산 세 값을 여기서 지우지 말 것** — 옛 행이 그 값을 여전히 들고 있고(과도기) admin 조회는
 * `INTERNAL`까지 **정당한 필터값**으로 받는다(서버 `PublicReleaseNoteQueryDto` JSDoc). 이 유니온을
 * 좁히면 그 응답을 받는 자리가 조용히 거짓이 된다.
 *
 * ⚠️ **늘릴 때는 `Record<ReleaseNotePlatform, …>` 매핑을 함께 센다** — CI는 web을 Vite(esbuild)로
 * 빌드해 **타입을 보지 않으므로** 비exhaustive 매핑이 `error TS`인 채 배포된다. 현재 소비처는
 * `pages/release-notes/index.tsx`의 `PLATFORM_LABELS`·`PLATFORM_COLORS` 둘이다.
 */
export type ReleaseNotePlatform =
  | 'ALL'
  | 'WEB'
  | 'MOBILE'
  | 'APP'
  | 'ADMIN'
  | 'SERVER'
  | 'INTERNAL';
export type ReleaseNoteCategory = 'FEATURE' | 'IMPROVEMENT' | 'BUGFIX' | 'SECURITY';

export interface PublicReleaseNoteItem {
  id: string;
  title: string;
  content: string | null;
}

export interface PublicReleaseNotePlatformGroup {
  /** @deprecated 1단계 alias(`platforms[0]`) — 화면은 `platforms`를 읽는다. 서버 DTO JSDoc이 정본. */
  platform: ReleaseNotePlatform;
  /** 이 그룹의 surface 집합(서버 정렬 순). 「웹·앱 공통」 노트는 원소가 둘 이상이다(2단계부터). */
  platforms: ReleaseNotePlatform[];
  version: string | null;
  items: PublicReleaseNoteItem[];
}

export interface PublicReleaseNoteCategoryGroup {
  category: ReleaseNoteCategory;
  platforms: PublicReleaseNotePlatformGroup[];
}

export interface PublicReleaseNoteDateGroup {
  date: string;
  categories: PublicReleaseNoteCategoryGroup[];
}

export interface PublicReleaseNotesResponse {
  groups: PublicReleaseNoteDateGroup[];
  nextCursor: string | null;
}

export const releaseNotesApi = {
  /**
   * 공개 릴리즈 노트 목록 조회 (날짜 기반 페이지네이션, 인증 불필요)
   * GET /api/v1/release-notes?platforms=WEB,MOBILE,ALL&days=7&beforeDate=2026-02-05
   *
   * `platforms`는 **콤마로 이어** 보낸다 — 서버 DTO(`PublicReleaseNoteQueryDto.platforms`)가 그 형태를
   * 정본으로 받는다(axios 기본 직렬화 `platforms[]=`에 기대지 않는다). `ALL` 태그 노트는 서버가 늘
   * 포함하므로 여기서 더할 필요는 없지만, 넣어도 무해하다. 빈 배열·undefined는 「필터 없음」이다.
   *
   * ⚠️ 서버가 `forbidNonWhitelisted: true`라 **여기 보내는 키는 DTO에 있어야 한다** — 키를 늘릴 땐
   * 서버 DTO와 같은 커밋에서(2026-10-15 `platforms`가 그렇게 들어왔다).
   */
  getPublic: async (params?: {
    platforms?: ReleaseNotePlatform[];
    days?: number;
    beforeDate?: string;
  }): Promise<ApiResponse<PublicReleaseNotesResponse>> => {
    const { platforms, ...rest } = params ?? {};
    const query = {
      ...rest,
      ...(platforms && platforms.length > 0 ? { platforms: platforms.join(',') } : {}),
    };
    const response = await api.get<ApiResponse<PublicReleaseNotesResponse>>(
      '/release-notes',
      {
        params: query,
        skipAuth: true,
      } as Parameters<typeof api.get>[1] & { skipAuth: boolean }
    );
    return response.data;
  },
};

export default api;
